import React, { useEffect, useState } from 'react';
import { ChevronLeft20Regular, ChevronRight20Regular } from '@fluentui/react-icons';
import {
  MONTH_FULL_ES,
  MonthSelection,
  MonthSelectorBarProps,
  monthKey,
  monthsBetween,
  todaySelection,
} from './MonthSelectorBar.Functions';
import './MonthSelectorBar.Styles.css';

const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/**
 * Un año a la vista, un mes por tarjeta con cuántas liquidaciones tiene y una
 * barra proporcional. Click elige un mes; Ctrl suma o quita; Shift toma el rango.
 */
export const MonthSelectorBar: React.FC<MonthSelectorBarProps> = ({ selected, onChange, counts }) => {
  const today = todaySelection();
  const [year, setYear] = useState<number>(selected[0]?.year ?? today.year);
  const [anchor, setAnchor] = useState<MonthSelection | null>(selected[0] ?? null);

  // Si la selección cambia desde afuera a otro año, la vista lo sigue.
  useEffect(() => {
    if (selected[0] && !selected.some((item) => item.year === year)) setYear(selected[0].year);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.map((item) => item.year).join(',')]);

  const countOf = (month: number) => counts?.[monthKey(year, month)] ?? 0;
  const max = Math.max(1, ...Array.from({ length: 12 }, (_, index) => countOf(index + 1)));
  const isSelected = (month: number) => selected.some((item) => item.year === year && item.month === month);
  const isToday = (month: number) => year === today.year && month === today.month;
  const isOnToday = selected.length === 1 && selected[0].year === today.year && selected[0].month === today.month;

  const pick = (month: number, event: React.MouseEvent) => {
    const clicked: MonthSelection = { year, month };
    if (event.shiftKey && anchor) {
      onChange(monthsBetween(anchor, clicked));
      return;
    }
    if (event.ctrlKey || event.metaKey) {
      const exists = isSelected(month);
      const next = exists ? selected.filter((item) => !(item.year === year && item.month === month)) : [...selected, clicked];
      onChange(next.length > 0 ? next : [clicked]);
    } else {
      onChange([clicked]);
    }
    setAnchor(clicked);
  };

  const goToday = () => {
    setYear(today.year);
    setAnchor(today);
    onChange([today]);
  };

  return (
    <div className="msb-root">
      <div className="msb-head">
        <div className="msb-year">
          <button type="button" className="msb-nav" aria-label="Año anterior" onClick={() => setYear((current) => current - 1)}><ChevronLeft20Regular /></button>
          <span className="msb-year__label">{year}</span>
          <button type="button" className="msb-nav" aria-label="Año siguiente" onClick={() => setYear((current) => current + 1)}><ChevronRight20Regular /></button>
        </div>
        <span className="msb-help">Click elige un mes · Ctrl suma otro · Shift toma el rango</span>
        <button type="button" className="msb-today" onClick={goToday} disabled={isOnToday}>Mes actual</button>
      </div>
      <div className="msb-grid">
        {MONTH_SHORT.map((label, index) => {
          const month = index + 1;
          const count = countOf(month);
          const selectedNow = isSelected(month);
          return (
            <button
              key={month}
              type="button"
              className={`msb-month${selectedNow ? ' is-selected' : ''}${count === 0 ? ' is-empty' : ''}${isToday(month) ? ' is-today' : ''}`}
              aria-pressed={selectedNow}
              aria-label={`${MONTH_FULL_ES[index]} ${year}${count > 0 ? ` (${count})` : ''}`}
              onClick={(event) => pick(month, event)}
            >
              <span className="msb-month__bar" aria-hidden>
                <span style={{ height: `${count > 0 ? Math.max(12, (count / max) * 100) : 0}%` }} />
              </span>
              <span className="msb-month__name">{label}</span>
              <span className="msb-month__count">{count > 0 ? count : '–'}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MonthSelectorBar;
